import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import { getPost, POSTS } from "@/lib/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <SEO title={`${post.title} — L F Building Blog`} description={post.excerpt} path={`/blog/${post.slug}`} />

      {/* HERO */}
      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-16 md:pb-24 border-b border-white/10">
        <div className="container-lf max-w-4xl space-y-6">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-brand transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/60 flex-wrap">
            <span className="text-brand font-semibold">{post.tag}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.read}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {post.author}</span>
          </div>
          <h1 className="font-display font-bold uppercase text-5xl md:text-7xl leading-[0.9]">{post.title}</h1>
          <p className="text-lg text-white/70 max-w-3xl">{post.excerpt}</p>
        </div>
      </section>

      {/* COVER */}
      <div className="bg-brand-ink pb-16 md:pb-24">
        <div className="container-lf">
          <div className="aspect-[16/8] overflow-hidden border border-white/10">
            <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* BODY */}
      <article className="py-20 md:py-28 bg-white" data-testid="blog-body">
        <div className="container-lf max-w-3xl">
          <div className="prose-lf space-y-6">
            {post.body.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2 key={i} className="font-display font-bold uppercase text-3xl md:text-4xl mt-10 leading-tight">
                    <span className="text-brand mr-3">/</span>{block.text}
                  </h2>
                );
              }
              return (
                <p key={i} className="text-lg leading-relaxed text-neutral-800">{block.text}</p>
              );
            })}
          </div>

          <div className="mt-14 pt-8 border-t border-black/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-neutral-500">
              Written by <span className="font-semibold text-brand-ink">{post.author}</span> · {post.date}
            </div>
            <Link to="/contact">
              <Button className="rounded-none bg-brand hover:bg-brand-hover text-white h-12 px-6 uppercase tracking-wider text-sm font-semibold">
                Get a Quote <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      <section className="py-16 md:py-24 bg-brand-alt border-y border-black/10" data-testid="blog-related">
        <div className="container-lf">
          <h2 className="font-display font-bold uppercase text-3xl md:text-4xl mb-10">Keep reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-black/10 hover:border-brand transition-colors bg-white overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-widest text-brand font-semibold mb-2">{p.tag}</div>
                  <h3 className="font-display font-bold uppercase text-xl leading-tight group-hover:text-brand transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Ready to build?" title="Get a free, no-obligation quote." />
    </>
  );
}
